import { ApolloError, useQuery } from "@apollo/client";
import Alert from "@mui/material/Alert";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import FormControlLabel from "@mui/material/FormControlLabel";
import InputBase from "@mui/material/InputBase";
import Paper from "@mui/material/Paper";
import { alpha, Theme } from "@mui/material/styles";
import Stack from "@mui/material/Stack";
import Switch from "@mui/material/Switch";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { DataGrid, DataGridProps, GridColDef, GridColumnGroupingModel, GridValidRowModel } from "@mui/x-data-grid";
import { ClientInvestmentClient, ClientInvestmentFund, ClientShareClassRow } from "_graphql-types";
import { ReactNode, useMemo } from "react";
import { useStore } from "react-admin";
import { CLIENT_INVESTMENT_CLIENTS, CLIENT_INVESTMENT_FUNDS, CLIENT_SHARE_CLASSES } from "./graphql";
import { formatMoney, formatPercent, previousMonth } from "./logic";

// The selection carried across the Client Investments pages, as the legacy app kept it in session.
const KEY = "clientInvestments.";
export const useSelectedFund = () => useStore<number | null>(`${KEY}fundId`, null);
export const useSelectedFunds = () => useStore<number[]>(`${KEY}fundIds`, []);
export const useSelectedClient = () => useStore<number | null>(`${KEY}clientId`, null);
export const useSelectedShareClass = () => useStore<number | null>(`${KEY}shareClassId`, null);
export const useSelectedMonth = () => useStore<string>(`${KEY}month`, previousMonth());

/** A page: its title, an optional one-line explanation, then its sections spaced apart. */
export const Page = ({ title, description, children }: { title: string; description?: ReactNode; children: ReactNode }) => (
  // react-admin's layout is min-width: fit-content, so a wide grid widened the whole page (its
  // toolbar and right-hand columns off screen); inline-size containment keeps the page to the
  // window and the grid scrolls inside it.
  <Box sx={{ p: 2, maxWidth: 1920, width: "100%", contain: "inline-size" }}>
    <Box sx={{ mb: 2 }}>
      <Typography variant="h5" component="h1">
        {title}
      </Typography>
      {description && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, maxWidth: 900 }}>
          {description}
        </Typography>
      )}
    </Box>
    <Stack spacing={2}>{children}</Stack>
  </Box>
);

/**
 * The page's selectors in one row that wraps. Its buttons take the height of the inputs on their
 * line, so they don't sit short beside them.
 */
export const FilterBar = ({ children }: { children: ReactNode }) => (
  <Paper variant="outlined" sx={{ p: 1.5 }}>
    <Stack direction="row" spacing={2} alignItems="flex-start" flexWrap="wrap" useFlexGap sx={{ "& > .MuiButton-root": { alignSelf: "stretch" } }}>
      {children}
    </Stack>
  </Paper>
);

/** A titled block of the page, with its actions on the right of the title. */
export const Section = ({ title, actions, children }: { title?: ReactNode; actions?: ReactNode; children: ReactNode }) => (
  <Paper variant="outlined" sx={{ p: 2 }}>
    {(title || actions) && (
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, flex: 1 }}>
          {title}
        </Typography>
        {actions}
      </Stack>
    )}
    {children}
  </Paper>
);

/** What to do before there is anything to show. */
export const EmptyState = ({ children }: { children: ReactNode }) => (
  <Paper variant="outlined" sx={{ p: 3, textAlign: "center", color: "text.secondary" }}>
    <Typography variant="body2">{children}</Typography>
  </Paper>
);

/** True when nothing is pending or the user agrees to lose it. */
export const confirmDiscard = (pending: number) =>
  pending === 0 || window.confirm(`Discard ${pending} unsaved change${pending === 1 ? "" : "s"}?`);

/** Save (with the count of changed rows) and Discard, for the editable grids. */
export function SaveActions({ pending, saving, onSave, onDiscard }: { pending: number; saving: boolean; onSave: () => void; onDiscard: () => void }) {
  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <Button disabled={pending === 0 || saving} onClick={() => confirmDiscard(pending) && onDiscard()}>
        Discard changes
      </Button>
      <Button variant="contained" disabled={pending === 0 || saving} onClick={onSave}>
        {saving ? "Saving..." : pending ? `Save changes (${pending})` : "Save"}
      </Button>
    </Stack>
  );
}

/** Loading spinner or the query's error; nothing once loaded. */
export const QueryState = ({ loading, error }: { loading: boolean; error?: ApolloError }) =>
  error ? <Alert severity="error">{error.message}</Alert> : loading ? <CircularProgress size={24} /> : null;

export const MonthField = ({ value, onChange, label = "Month" }: { value: string; onChange: (m: string) => void; label?: string }) => (
  <TextField
    type="month"
    label={label}
    size="small"
    value={value}
    onChange={e => e.target.value && onChange(e.target.value)}
    InputLabelProps={{ shrink: true }}
    sx={{ width: 180 }}
  />
);

function useFunds() {
  const { data, loading, error } = useQuery(CLIENT_INVESTMENT_FUNDS);
  const funds = useMemo(
    () => [...(data?.clientInvestmentFunds ?? [])].sort((a, b) => a.name.localeCompare(b.name)),
    [data]
  );
  return { funds, loading, error };
}

export function FundPicker({ value, onChange }: { value: number | null; onChange: (id: number | null) => void }) {
  const { funds, loading, error } = useFunds();
  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <Autocomplete<ClientInvestmentFund>
        sx={{ width: 420 }}
        size="small"
        loading={loading}
        options={funds}
        value={funds.find(f => f.rcgFundId === value) ?? null}
        onChange={(_, f) => onChange(f?.rcgFundId ?? null)}
        getOptionLabel={f => f.name}
        isOptionEqualToValue={(a, b) => a.rcgFundId === b.rcgFundId}
        renderOption={(props, f) => (
          <li {...props} key={f.rcgFundId}>
            <Box>
              {f.name}
              <Typography variant="caption" color="text.secondary" display="block">
                {f.groupName}
              </Typography>
            </Box>
          </li>
        )}
        renderInput={params => <TextField {...params} label="RCG Fund" />}
      />
      {error && <Alert severity="error">{error.message}</Alert>}
    </Stack>
  );
}

/** Pick funds one at a time, or a whole group at once. */
export function MultiFundPicker({ value, onChange }: { value: number[]; onChange: (ids: number[]) => void }) {
  const { funds, loading, error } = useFunds();
  const groups = useMemo(() => {
    const byName = new Map<string, number[]>();
    for (const f of funds) byName.set(f.groupName, [...(byName.get(f.groupName) ?? []), f.rcgFundId]);
    return [...byName.entries()].sort(([a], [b]) => a.localeCompare(b)).map(([name, ids]) => ({ name, ids }));
  }, [funds]);
  const groupOrder = (f: ClientInvestmentFund) => `${f.groupName}\u0000${f.name}`;
  return (
    <Stack direction="row" spacing={1} alignItems="flex-start">
      <Autocomplete<ClientInvestmentFund, true>
        multiple
        sx={{ width: 520 }}
        size="small"
        loading={loading}
        // A group can be dozens of funds: past a few, one chip with the count (cleared by its x, or
        // opened to untick funds one by one).
        renderTags={(selected, getTagProps) =>
          selected.length > 3 ? (
            <Chip size="small" label={`${selected.length} funds`} onDelete={() => onChange([])} />
          ) : (
            selected.map((f, index) => {
              const { key, ...tagProps } = getTagProps({ index });
              return <Chip key={key} size="small" label={f.name} {...tagProps} />;
            })
          )
        }
        options={[...funds].sort((a, b) => groupOrder(a).localeCompare(groupOrder(b)))}
        groupBy={f => f.groupName}
        value={funds.filter(f => value.includes(f.rcgFundId))}
        onChange={(_, fs) => onChange(fs.map(f => f.rcgFundId))}
        getOptionLabel={f => f.name}
        isOptionEqualToValue={(a, b) => a.rcgFundId === b.rcgFundId}
        disableCloseOnSelect
        renderInput={params => (
          <TextField {...params} label="RCG Funds" placeholder={value.length ? undefined : "Pick funds"} />
        )}
      />
      <Autocomplete<{ name: string; ids: number[] }>
        sx={{ width: 260 }}
        size="small"
        options={groups}
        value={null}
        onChange={(_, g) => g && onChange([...new Set([...value, ...g.ids])])}
        getOptionLabel={g => `${g.name} (${g.ids.length})`}
        renderInput={params => <TextField {...params} label="Add a group" />}
      />
      {error && <Alert severity="error">{error.message}</Alert>}
    </Stack>
  );
}

export function ClientPicker({
  rcgFundId,
  value,
  onChange,
  allClients = false,
}: {
  rcgFundId: number | null;
  value: number | null;
  onChange: (id: number | null) => void;
  /** Every client, not only those with a share class in the fund. */
  allClients?: boolean;
}) {
  const { data, loading, error } = useQuery(CLIENT_INVESTMENT_CLIENTS, {
    variables: { rcgFundId: allClients ? null : rcgFundId },
    skip: !allClients && rcgFundId === null,
  });
  const clients = useMemo(
    () => [...(data?.clientInvestmentClients ?? [])].sort((a, b) => a.name.localeCompare(b.name)),
    [data]
  );
  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <Autocomplete<ClientInvestmentClient>
        sx={{ width: 360 }}
        size="small"
        loading={loading}
        options={clients}
        value={clients.find(c => c.clientId === value) ?? null}
        onChange={(_, c) => onChange(c?.clientId ?? null)}
        getOptionLabel={c => c.name}
        isOptionEqualToValue={(a, b) => a.clientId === b.clientId}
        renderOption={(props, c) => (
          <li {...props} key={c.clientId}>
            {c.name}
          </li>
        )}
        renderInput={params => <TextField {...params} label="Client" />}
      />
      {error && <Alert severity="error">{error.message}</Alert>}
    </Stack>
  );
}

export const AllClientsToggle = ({ value, onChange }: { value: boolean; onChange: (v: boolean) => void }) => (
  <FormControlLabel control={<Switch checked={value} onChange={e => onChange(e.target.checked)} />} label="All clients" />
);

/** A fund's share classes for one client, ordered by name. */
export function useShareClasses(rcgFundId: number | null, clientId: number | null) {
  const { data, loading, error, refetch } = useQuery(CLIENT_SHARE_CLASSES, {
    variables: { rcgFundId: rcgFundId ?? 0, clientId },
    skip: rcgFundId === null || clientId === null,
  });
  const shareClasses = useMemo(
    () => [...(data?.clientShareClasses ?? [])].sort((a, b) => a.name.localeCompare(b.name)),
    [data]
  );
  return { shareClasses, loading, error, refetch };
}

export function ShareClassPicker({
  shareClasses,
  value,
  onChange,
  label = "Share class",
}: {
  shareClasses: ClientShareClassRow[];
  value: number | null;
  onChange: (id: number | null) => void;
  label?: string;
}) {
  return (
    <Autocomplete<ClientShareClassRow>
      sx={{ width: 320 }}
      size="small"
      options={shareClasses}
      value={shareClasses.find(s => s.shareClassId === value) ?? null}
      onChange={(_, s) => onChange(s?.shareClassId ?? null)}
      getOptionLabel={s => (s.derived ? `${s.name} (derived)` : s.name)}
      isOptionEqualToValue={(a, b) => a.shareClassId === b.shareClassId}
      renderInput={params => <TextField {...params} label={label} />}
    />
  );
}

// ---- DataGrid columns ---------------------------------------------------------------------------

export const TOTAL_ROW_ID = "total";

/** A dollar column. */
export const moneyCol = <R,>(field: string, headerName: string, width = 130): GridColDef<R & object> => ({
  field,
  headerName,
  type: "number",
  width,
  valueFormatter: (v: number | null | undefined) => formatMoney(v),
});

/** A column already in percent units. */
export const percentCol = <R,>(field: string, headerName: string, width = 110): GridColDef<R & object> => ({
  field,
  headerName,
  type: "number",
  width,
  valueFormatter: (v: number | null | undefined) => formatPercent(v),
});

/**
 * Rows' ids where their group (e.g. RCG fund) starts, for rows already ordered by it: the grid draws
 * a rule above them and shows the group's name only there.
 */
export const groupStarts = <R extends { id: string | number }>(rows: readonly R[], key: (r: R) => string) =>
  new Set(rows.filter((r, i) => i === 0 || key(rows[i - 1]) !== key(r)).map(r => r.id));

/** A name column shown only on the first row of its group (the export still has it on every row). */
export const groupCol = <R extends { id: string | number }>(
  field: string,
  headerName: string,
  starts: ReadonlySet<string | number>,
  width = 220
): GridColDef<R> => ({
  field,
  headerName,
  width,
  renderCell: ({ row, formattedValue }) =>
    starts.has(row.id) ? (
      <Box component="span" title={String(formattedValue ?? "")} sx={{ fontWeight: 600 }}>
        {formattedValue}
      </Box>
    ) : null,
});

/** A name column: ellipsis when it doesn't fit, the full text on hover. */
export const textCol = <R,>(field: string, headerName: string, width = 200): GridColDef<R & object> => ({
  field,
  headerName,
  width,
  renderCell: ({ formattedValue }) => <span title={String(formattedValue ?? "")}>{formattedValue}</span>,
});

/** Band headers over the columns that are present (a hidden column's field is dropped). */
export function columnGroups(
  columns: ReadonlyArray<{ field: string }>,
  groups: ReadonlyArray<{ groupId: string; headerName?: string; fields: readonly string[] }>
): GridColumnGroupingModel {
  const present = new Set(columns.map(c => c.field));
  return groups
    .map(g => ({
      groupId: g.groupId,
      headerName: g.headerName ?? g.groupId,
      headerAlign: "center" as const,
      children: g.fields.filter(f => present.has(f)).map(field => ({ field })),
    }))
    .filter(g => g.children.length > 0);
}

/** A typed value in an editable grid cell; outlined, and highlighted once it differs from what was loaded. */
export function EntryInput({
  value,
  initial,
  onChange,
  label,
  suffix,
  placeholder,
  title,
}: {
  value: string;
  initial: string;
  onChange: (value: string) => void;
  label: string;
  suffix?: string;
  placeholder?: string;
  title?: string;
}) {
  const changed = value.trim() !== initial;
  return (
    <InputBase
      value={value}
      placeholder={placeholder}
      title={changed ? `Changed (was ${initial === "" ? "blank" : initial})` : title}
      onChange={e => onChange(e.target.value)}
      // The grid's keyboard navigation would otherwise take arrows and spaces.
      onKeyDown={e => e.stopPropagation()}
      endAdornment={suffix}
      inputProps={{ inputMode: "decimal", style: { textAlign: "right" }, "aria-label": label }}
      sx={{
        fontSize: "inherit",
        width: "100%",
        px: 0.75,
        border: 1,
        borderRadius: 1,
        borderColor: changed ? "warning.main" : "divider",
        bgcolor: t => (changed ? alpha(t.palette.warning.main, 0.15) : t.palette.background.paper),
        fontWeight: changed ? 600 : undefined,
        "&:hover": { borderColor: changed ? "warning.dark" : "text.secondary" },
        "&.Mui-focused": { borderColor: "primary.main", boxShadow: t => `0 0 0 1px ${t.palette.primary.main}` },
      }}
    />
  );
}

/** A value that can't be entered here, with why on hover. */
export const ReadOnlyCell = ({ text, reason }: { text: string; reason: string }) => (
  <Box component="span" title={reason} sx={{ width: "100%", textAlign: "right", color: "text.disabled", cursor: "not-allowed" }}>
    {text || "\u2014"}
  </Box>
);

const gridSx = {
  "& .MuiDataGrid-columnHeaderTitle": { fontWeight: 600 },
  // The totals row: bold, shaded, set off by a rule.
  "& .total-row, & .total-row:hover": { fontWeight: "bold", bgcolor: "action.selected" },
  "& .total-row .MuiDataGrid-cell": { borderTop: 2, borderTopColor: "divider" },
  // The first row of each group (fund).
  "& .group-start .MuiDataGrid-cell": { borderTop: 2, borderTopColor: "divider" },
  // The row being typed in stands out, so it's clear which row the cell is on.
  "& .MuiDataGrid-row:focus-within": { bgcolor: (t: Theme) => alpha(t.palette.primary.main, 0.08) },
};

/**
 * The pages' grid: compact, toolbar with CSV export, totals row set off, cell borders, rows in the
 * API's order (no column sorting, which would move the totals row). It grows with its rows up to
 * 70% of the window, then scrolls inside so the headers stay in view. The free grid pages at 100
 * rows at most.
 */
export function ReportGrid<R extends GridValidRowModel>({
  noRowsLabel,
  groupStarts: starts,
  ...props
}: Omit<DataGridProps<R>, "sx" | "getRowClassName"> & { noRowsLabel?: string; groupStarts?: ReadonlySet<string | number> }) {
  return (
    // ponytail: a flex parent with a max height is the v8 replacement for autoHeight.
    <Box sx={{ display: "flex", flexDirection: "column", maxHeight: "70vh", minHeight: 160, width: "100%", minWidth: 0 }}>
      <DataGrid<R>
        density="compact"
        showToolbar
        showCellVerticalBorder
        showColumnVerticalBorder
        disableRowSelectionOnClick
        disableColumnSorting
        pageSizeOptions={[25, 50, 100]}
        initialState={{ pagination: { paginationModel: { pageSize: 100 } } }}
        getRowClassName={({ id }) => (id === TOTAL_ROW_ID ? "total-row" : starts?.has(id) && id !== props.rows?.[0]?.id ? "group-start" : "")}
        localeText={noRowsLabel ? { noRowsLabel } : undefined}
        {...props}
        sx={gridSx}
      />
    </Box>
  );
}
